import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Star, StarOff, Printer, RotateCcw, ChevronRight, ExternalLink, FileText, Download, ArrowLeftRight, Home } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { API } from "@/config/constants";
import { ProtocolLinkContext } from "@/contexts/ProtocolLinkContext";
import { useOfflineData } from "@/contexts/OfflineDataContext";
import CareLevelTabs from "@/components/protocol/CareLevelTabs";
import InfoBox from "@/components/protocol/InfoBox";
import ExpandableImageGallery from "@/components/protocol/ExpandableImageGallery";
import SMRAlgorithm from "@/components/protocol/SMRAlgorithm";
import CIWACalculator from "@/components/protocol/CIWACalculator";
import APGARCalculator from "@/components/protocol/APGARCalculator";
import GCSCalculator from "@/components/protocol/GCSCalculator";
import MAPCalculator from "@/components/protocol/MAPCalculator";
import ShockIndexCalculator from "@/components/protocol/ShockIndexCalculator";
import ThrombolyticChecklist from "@/components/protocol/ThrombolyticChecklist";
import CodeTimerButton from "@/components/protocol/CodeTimerButton";
import ProtocolReferences from "@/components/protocol/ProtocolReferences";
import { ProtocolCalculator, protocolHasWeightDosing } from "@/components/protocol/WeightDoseCalculator";
import InterventionQuickAdd from "@/components/protocol/InterventionQuickAdd";
import BSACalculator from "@/components/protocol/BSACalculator";
import CIWACalculatorInline from "@/components/protocol/CIWACalculatorInline";
import BloodTransfusionTimer from "@/components/protocol/BloodTransfusionTimer";
import RSIChecklist from "@/components/protocol/RSIChecklist";
import VentilatorTool from "@/components/protocol/VentilatorTool";
import InfusionRateCalculator from "@/components/protocol/InfusionRateCalculator";
import ProtocolVideos from "@/components/protocol/ProtocolVideos";
import BroselowEstimator from "@/components/protocol/BroselowEstimator";

import { useService } from "@/contexts/ServiceContext";
import { useWeight } from "@/contexts/WeightContext";

const APGAR_PROTOCOL_IDS = [
  "normal-delivery", "labour-delivery", "pre-term-labour", "breech-delivery",
  "neonatal-resuscitation", "postpartum-hemorrhage",
  "sfa-emr-labour-delivery", "sfa-emr-breech-delivery", "sfa-emr-neonatal-resuscitation",
  "air-peds-labour", "air-peds-breech-delivery", "air-peds-neonatal-resuscitation",
  "air-peds-shoulder-dystocia", "air-peds-cord-limb-presentation",
  "air-peds-preterm-labour", "air-peds-postpartum-hemorrhage",
  "air-peds-neonatal-temperature", "air-peds-ob-assessment",
];

const CODE_TIMER_PROTOCOL_IDS = [
  "cardiac-arrest", "opioid-cardiac-arrest", "rosc", "hypothermic-arrest",
  "pediatric-cardiac-arrest", "pediatric-rosc",
  "sfa-emr-cardiac-arrest", "sfa-emr-rosc", "sfa-emr-pediatric-cardiac-arrest",
  "wes-cardiac-arrest", "wes-hypothermic-arrest", "wes-opioid-cardiac-arrest", "wes-diabetic-cardiac-arrest", "wes-rosc",
  "air-adult-pulseless-arrest", "air-adult-hypothermic-arrest", "air-adult-rosc",
  "air-peds-cardiac-arrest", "air-peds-rosc",
];

const SHOCK_INDEX_PROTOCOL_IDS = [
  "shock-hypotension", "anaphylactic-shock", "cardiogenic-shock", "hypovolemia", "sepsis",
  "neonatal-hypoperfusion-shock", "pediatric-shock-hypotension", "pediatric-anaphylactic-shock",
  "sfa-emr-anaphylactic-shock", "sfa-emr-hypovolemia",
  "wes-anaphylactic-shock", "wes-hypotension", "wes-hypovolemia", "wes-sepsis",
  "air-adult-anaphylactic-shock", "air-adult-cardiogenic-shock", "air-adult-hypotension", "air-adult-sepsis",
  "air-peds-neonatal-shock", "air-peds-sepsis", "air-peds-shock-management",
];

const TNK_CHECKLIST_PROTOCOL_IDS = [
  "acute-coronary-syndrome",
  "air-adult-acs",
  "air-adult-thrombolysis",
  "air-adult-post-thrombolytic",
  "tenecteplase-flowsheet",
];

const BURN_PROTOCOL_IDS = [
  "burns", "pediatric-burns",
  "sfa-emr-burns", "wes-burns",
  "air-adult-burns", "air-peds-burn-management",
];

const CIWA_PROTOCOL_IDS = [
  "alcohol-withdrawal", "wes-alcohol-withdrawal", "air-adult-alcohol-withdrawal",
];

export default function ProtocolDetail({ favorites, onToggleFavorite, onViewProtocol }) {
  const { id } = useParams();
  const [protocol, setProtocol] = useState(null);
  const [checkedItems, setCheckedItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  const fromProtocol = location.state?.fromProtocol;
  const { protocols: allProtocols, getProtocol } = useOfflineData();
  const { concentrations, infusionDosing, allowedServices, drugIndications } = useWeight();

  // Map backend service ID → allowed_services tag used in concentration config
  const serviceTag = (s) => ({
    "ground": "ground",
    "air_adult": "air-adult",
    "air_pediatric": "air-peds",
    "wes": null,        // PCP-PS — no infusion drips per clinical scope
    "sfa_emr": null,    // SFA/EMR — no infusion drips per clinical scope
  }[s] ?? null);

  // Per-protocol exclusions (clinical safety: don't show epi drip in anaphylaxis even though service allows)
  const PROTOCOL_INFUSION_EXCLUDES = {
    "anaphylaxis": ["epinephrine"],
    "anaphylactic-shock": ["epinephrine"],
  };
  // Per-protocol forced INCLUDES (always render these calculators for the protocol if service permits)
  const PROTOCOL_INFUSION_INCLUDES = {
    "acute-pulmonary-edema": ["nitroglycerin"],
    "cardiogenic-shock": ["norepinephrine", "dopamine"],
  };

  // Detect which infusion drugs are referenced AND allowed for this protocol's service
  const infusionDrugsInProtocol = React.useMemo(() => {
    if (!protocol || !infusionDosing) return [];
    const tag = serviceTag(protocol.service);
    if (!tag) return []; // PCP-PS / SFA/EMR — no infusion drips
    const excludes = PROTOCOL_INFUSION_EXCLUDES[protocol.id] || [];
    const forcedIncludes = PROTOCOL_INFUSION_INCLUDES[protocol.id] || [];
    const drugs = Object.keys(infusionDosing);
    if (drugs.length === 0) return [];
    const haystack = JSON.stringify(protocol.care_levels || []).toLowerCase();
    // Drug-name aliases that should also count as a match in the protocol text
    const DRUG_ALIASES = {
      nitroglycerin: ["nitro", "ntg", "gtn"],
      norepinephrine: ["levophed"],
      epinephrine: ["epi"],
    };
    return drugs.filter((d) => {
      if (excludes.includes(d)) return false;
      const allowed = allowedServices?.[d];
      if (allowed && !allowed.includes(tag)) return false;
      if (forcedIncludes.includes(d)) return true;
      // Auto-include if the protocol id is listed in the drug's clinical indications
      const indications = drugIndications?.[d] || [];
      if (indications.includes(protocol.id)) return true;
      // Match drug name or any alias inside protocol text
      const names = [d, ...(DRUG_ALIASES[d] || [])];
      return names.some((n) => {
        const re = new RegExp(`\\b${n.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\b`, "i");
        return re.test(haystack);
      });
    });
  }, [protocol, infusionDosing, allowedServices, drugIndications]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Use offline-capable data fetching
        const protocolData = await getProtocol(id);
        if (!protocolData) {
          toast.error("Protocol not found");
          navigate("/");
          return;
        }
        setProtocol(protocolData);
        
        // Track in recent history
        if (onViewProtocol) {
          onViewProtocol(protocolData);
        }
        // Checklist is user-specific, try network only
        try {
          const checklistRes = await axios.get(`${API}/checklists/${id}`);
          setCheckedItems(checklistRes.data.completed_steps || []);
        } catch {
          setCheckedItems([]);
        }
      } catch (error) {
        console.error("Error fetching protocol:", error);
        toast.error("Protocol not found");
        navigate("/");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id, navigate, getProtocol, onViewProtocol]);

  const handleToggleItem = async (itemId) => {
    const newChecked = checkedItems.includes(itemId)
      ? checkedItems.filter(i => i !== itemId)
      : [...checkedItems, itemId];
    setCheckedItems(newChecked);
    try {
      await axios.post(`${API}/checklists/${id}`, newChecked, {
        headers: { 'Content-Type': 'application/json' }
      });
    } catch (error) {
      console.error("Error saving checklist:", error);
    }
  };

  const handleClearChecklist = async () => {
    setCheckedItems([]);
    try {
      await axios.delete(`${API}/checklists/${id}`);
      toast.success("Checklist cleared");
    } catch (error) {
      console.error("Error clearing checklist:", error);
    }
  };

  const handlePrint = () => { window.print(); };

  const getReferencedProtocols = () => {
    if (!protocol?.references) return [];
    return allProtocols.filter(p => protocol.references.includes(p.id));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse text-muted-foreground">Loading protocol...</div>
      </div>
    );
  }

  if (!protocol) return null;

  const isFavorite = favorites.some(f => f.protocol_id === protocol.id);
  const referencedProtocols = getReferencedProtocols();
  const linkContextValue = { protocols: allProtocols, currentProtocol: protocol };

  return (
    <ProtocolLinkContext.Provider value={linkContextValue}>
      <div className="max-w-4xl mx-auto space-y-6">
        {protocol.service === "sfa_emr" && (
          <Link 
            to="/"
            className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors"
            data-testid="return-to-dashboard-link"
          >
            <Home className="w-4 h-4" />
            Return to SFA/EMR Dashboard
          </Link>
        )}
        {fromProtocol && (
          <Link 
            to={`/protocol/${fromProtocol.id}`}
            className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors"
            data-testid="back-to-protocol-link"
          >
            <ChevronRight className="w-4 h-4 rotate-180" />
            Back to {fromProtocol.name}
          </Link>
        )}
        
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
          <div className="min-w-0">
            <h1 className="font-heading text-2xl sm:text-3xl font-bold break-words" data-testid="protocol-title">{protocol.name}</h1>
            <div className="flex items-center gap-2 mt-2">
              <Link to={`/category/${protocol.category}`} data-testid="category-back-link">
                <Badge variant="outline" className="cursor-pointer hover:bg-primary/10 transition-colors">{protocol.category}</Badge>
              </Link>
            </div>
          </div>
          
          <div className="flex gap-2 no-print shrink-0 flex-wrap">
            <GCSCalculator />
            <MAPCalculator />
            {APGAR_PROTOCOL_IDS.includes(protocol.id) && <APGARCalculator />}
            {(protocol.id === "alcohol-withdrawal" || protocol.id === "wes-alcohol-withdrawal") && (
              <CIWACalculator variant={protocol.id.startsWith("wes-") ? "wes" : "ground"} />
            )}
            {SHOCK_INDEX_PROTOCOL_IDS.includes(protocol.id) && <ShockIndexCalculator />}
            {TNK_CHECKLIST_PROTOCOL_IDS.includes(protocol.id) && <ThrombolyticChecklist />}
            {CODE_TIMER_PROTOCOL_IDS.includes(protocol.id) && <CodeTimerButton />}
            <InterventionQuickAdd protocol={protocol} />
            <Button variant="outline" size="icon" onClick={() => onToggleFavorite(protocol.id)} data-testid="protocol-favorite-btn">
              {isFavorite ? (
                <Star className="h-4 w-4 fill-yellow-500 text-yellow-500" />
              ) : (
                <StarOff className="h-4 w-4" />
              )}
            </Button>
            <Button variant="outline" size="icon" onClick={handlePrint} data-testid="print-btn">
              <Printer className="h-4 w-4" />
            </Button>
            <Button variant="outline" size="icon" onClick={handleClearChecklist} data-testid="clear-checklist-btn">
              <RotateCcw className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Adult/Pediatric Link */}
        {protocol.related_age_protocol && (
          <Link
            to={`/protocol/${protocol.related_age_protocol.id}`}
            className="flex items-center gap-2 p-3 rounded-lg border border-primary/30 bg-primary/5 hover:bg-primary/10 transition-colors"
            data-testid="age-group-link"
          >
            <ArrowLeftRight className="w-4 h-4 text-primary shrink-0" />
            <span className="text-sm font-medium">
              See also: <span className="text-primary">{protocol.related_age_protocol.label}</span>
            </span>
            <ChevronRight className="w-4 h-4 text-primary ml-auto" />
          </Link>
        )}

        {/* Related Protocols */}
        {protocol.related_protocols?.length > 0 && (
          <div className="flex flex-wrap gap-2" data-testid="related-protocols">
            {protocol.related_protocols.map((rp) => (
              <Link
                key={rp.id}
                to={`/protocol/${rp.id}`}
                className="flex items-center gap-2 p-3 rounded-lg border border-blue-500/30 bg-blue-500/5 hover:bg-blue-500/10 transition-colors flex-1 min-w-[200px]"
              >
                <FileText className="w-4 h-4 text-blue-500 shrink-0" />
                <span className="text-sm font-medium">
                  Related: <span className="text-blue-500">{rp.name}</span>
                </span>
                <ChevronRight className="w-4 h-4 text-blue-500 ml-auto" />
              </Link>
            ))}
          </div>
        )}

        {/* Broselow Weight Estimator for pediatric protocols */}
        {(protocol.id?.includes('pediatric') || protocol.id?.includes('peds') || protocol.id?.includes('neonatal')) && (
          <BroselowEstimator />
        )}

        {/* Care Levels */}
        <CareLevelTabs careLevelData={protocol.care_levels} />

        {/* CIWA Calculator — embedded inline for alcohol withdrawal protocols */}
        {CIWA_PROTOCOL_IDS.includes(protocol.id) && (
          <CIWACalculatorInline variant={
            protocol.id.startsWith("wes-") ? "wes" :
            protocol.id.startsWith("air-") ? "air" : "ground"
          } />
        )}

        {/* Blood Transfusion Timer/Checklist */}
        {protocol.id === "air-adult-blood-transfusion" && <BloodTransfusionTimer />}

        {/* RSI Checklist */}
        {(protocol.id === "difficult-airway-management" || protocol.id === "rsi-checklist" || protocol.id === "air-adult-difficult-airway") && <RSIChecklist />}

        {/* Hamilton T1 Ventilator Tool — Air Ambulance airway/ventilation protocols */}
        {[
          "air-adult-airway-management",
          "air-adult-advanced-airway",
          "air-peds-advanced-airway",
          "air-peds-mechanical-ventilation-ref",
        ].includes(protocol.id) && (
          <VentilatorTool defaultPopulation={protocol.id.startsWith("air-peds-") ? "peds" : "adult"} />
        )}

        {/* Infusion Rate Calculators — auto-rendered for drugs with infusion_dosing referenced in this protocol */}
        {infusionDrugsInProtocol.map((drug) => (
          <InfusionRateCalculator
            key={drug}
            drug={drug}
            options={concentrations[drug] || []}
            dosing={infusionDosing[drug]}
          />
        ))}

        {/* Weight Calculator */}
        {protocolHasWeightDosing(protocol) && (
          <ProtocolCalculator interventions={protocol.care_levels.flatMap(level => level.interventions || [])} />
        )}

        {/* SMR Algorithm */}
        {protocol.id === "smr-rule-out" && <SMRAlgorithm />}

        {/* Info Boxes — with BSA Calculator after first box for burn protocols */}
        {protocol.info_boxes && protocol.info_boxes.length > 0 && (
          <div className="space-y-3" data-testid="protocol-info-boxes">
            {protocol.info_boxes.map((box, idx) => (
              <React.Fragment key={idx}>
                <InfoBox type={box.type} title={box.title} content={box.content} image={box.image} imageAlt={box.imageAlt} />
                {idx === 0 && BURN_PROTOCOL_IDS.includes(protocol.id) && <BSACalculator />}
              </React.Fragment>
            ))}
          </div>
        )}
        {/* BSA Calculator fallback if no info boxes */}
        {(!protocol.info_boxes || protocol.info_boxes.length === 0) && BURN_PROTOCOL_IDS.includes(protocol.id) && <BSACalculator />}

        {/* Protocol Videos */}
        <ProtocolVideos protocolId={protocol.id} />

        {/* Images */}
        <ExpandableImageGallery images={protocol.images} />

        {/* Referenced Protocols */}
        {referencedProtocols.length > 0 && (
          <Card className="no-print">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg font-heading flex items-center gap-2">
                <ExternalLink className="w-4 h-4" />
                Related Protocols
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {referencedProtocols.map(ref => (
                  <Link key={ref.id} to={`/protocol/${ref.id}`} state={{ fromProtocol: protocol }}>
                    <Button variant="outline" size="sm" data-testid={`ref-protocol-${ref.id}`}>
                      {ref.name}
                      <ChevronRight className="w-3 h-3 ml-1" />
                    </Button>
                  </Link>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Source Document */}
        {protocol.source_ref && (
          <Card className="no-print border-muted">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg font-heading flex items-center gap-2">
                <FileText className="w-4 h-4" />
                Source Document
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="text-sm">
                  <p className="font-medium text-foreground">{protocol.source_ref.document}</p>
                  <p className="text-muted-foreground">
                    Section: {protocol.source_ref.section} | Pages: {protocol.source_ref.pages}
                  </p>
                </div>
                <a
                  href={`${API}/document/download`}
                  download
                  className="inline-flex items-center gap-2 text-sm text-primary hover:underline"
                  data-testid="download-source-doc"
                >
                  <Download className="w-4 h-4" />
                  Download Source Document (.docx)
                </a>
              </div>
            </CardContent>
          </Card>
        )}

        {/* References */}
        <ProtocolReferences references={protocol.research_references} />
      </div>
    </ProtocolLinkContext.Provider>
  );
}
